import { z } from "zod";

// Subject options for contact form
export const subjectOptions = [
  "Local Authority Searches",
  "Environmental Searches",
  "Water & Drainage Reports",
  "Compliance & Risk Services",
  "Technology Solutions",
  "Insurance & Protection",
  "General Enquiry",
  "Account Setup",
  "Technical Support",
  "Other",
] as const;

// UK phone number validation regex
// Supports: 07xxx, +447xxx, 01xxx, 02xxx, 03xxx (landlines and mobiles)
const ukPhoneRegex = /^(?:(?:\+44\s?|0)(?:7\d{3}|\d{4})\s?\d{3}\s?\d{3,4}|\+44\s?\d{2,4}\s?\d{3,4}\s?\d{3,4})$/;

// Helper to validate UK phone numbers
export const isValidUKPhone = (phone: string): boolean => {
  if (!phone || phone.trim() === "") return true; // Optional field
  const cleaned = phone.replace(/\s/g, "");
  return ukPhoneRegex.test(cleaned) || cleaned.length >= 10;
};

// Contact form schema
export const contactFormSchema = z.object({
  name: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters")
    .regex(
      /^[a-zA-Z\s\-'\.]+$/,
      "Name can only contain letters, spaces, hyphens, apostrophes, and periods"
    ),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(254, "Email must be less than 254 characters")
    .transform((val) => val.toLowerCase().trim()),
  company: z
    .string()
    .max(200, "Company name must be less than 200 characters")
    .optional()
    .or(z.literal("")),
  phone: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine(
      (val) => {
        if (!val || val.trim() === "") return true;
        const cleaned = val.replace(/[\s\-\(\)]/g, "");
        // Accept UK formats: 07xxx (11 digits), +447xxx (12 digits with +), landlines
        return cleaned.length >= 10 && cleaned.length <= 15;
      },
      { message: "Please enter a valid UK phone number" }
    ),
  subject: z.enum(subjectOptions, {
    errorMap: () => ({ message: "Please select a subject" }),
  }),
  message: z
    .string()
    .min(10, "Message must be at least 10 characters")
    .max(2000, "Message must be less than 2000 characters"),
  newsletter: z.boolean().default(false),
});

// Newsletter schema (email only)
export const newsletterSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(254, "Email must be less than 254 characters")
    .transform((val) => val.toLowerCase().trim()),
});

// Type exports
export type ContactFormData = z.infer<typeof contactFormSchema>;
export type NewsletterFormData = z.infer<typeof newsletterSchema>;

// Helper to get field error message
export const getFieldError = (
  errors: Record<string, { message?: string } | undefined>,
  field: string
): string | undefined => {
  return errors[field]?.message;
};

// Helper to format Zod errors for API responses
export const formatZodErrors = (
  error: z.ZodError
): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};
  error.errors.forEach((err) => {
    const path = err.path.join(".");
    if (path && !fieldErrors[path]) {
      fieldErrors[path] = err.message;
    }
  });
  return fieldErrors;
};
